/**
 * NAT/Firewall detection for voice agent playground
 * Detects when a user's network blocks STUN, preventing WebRTC calls from working.
 * https://linear.app/plivo-cx/issue/SER-4713
 */
import { useCallback, useEffect, useState } from 'react'
import { isEmbedMode } from 'v2/shared/utils/embed-session'
import { agentLogger } from 'v2/utils/logger'

const STUN_SERVER = 'stun:stun.l.google.com:19302'
const ICE_GATHERING_TIMEOUT = 2000
const SESSION_STORAGE_KEY = 'cx-nat-detection'

interface NatDetectionResult {
  isBehindNat: boolean
  isChecking: boolean
  hasError: boolean
  publicIp: string | null
}

interface CachedResult {
  isBehindNat: boolean
  publicIp: string | null
}

const defaultResult: NatDetectionResult = {
  isBehindNat: false,
  isChecking: false,
  hasError: false,
  publicIp: null,
}

function getCachedResult(): CachedResult | null {
  try {
    const cached = sessionStorage.getItem(SESSION_STORAGE_KEY)
    if (!cached) return null
    return JSON.parse(cached) as CachedResult
  } catch {
    return null
  }
}

function setCachedResult(result: CachedResult): void {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(result))
  } catch {
    // sessionStorage full or disabled
  }
}

function isPrivateIp(ip: string): boolean {
  const parts = ip.split('.').map(Number)
  if (parts.length !== 4) return false
  if (parts[0] === 10) return true // 10.0.0.0/8
  if (parts[0] === 172 && parts[1] >= 16 && parts[1] <= 31) return true // 172.16.0.0/12
  if (parts[0] === 192 && parts[1] === 168) return true // 192.168.0.0/16
  if (parts[0] === 127) return true // 127.0.0.0/8 loopback
  if (parts[0] === 169 && parts[1] === 254) return true // 169.254.0.0/16 link-local
  if (parts[0] === 100 && parts[1] >= 64 && parts[1] <= 127) return true // 100.64.0.0/10 CGNAT
  return false
}

function checkStunConnectivity(): Promise<{ isBehindNat: boolean; publicIp: string | null }> {
  return new Promise((resolve) => {
    if (typeof RTCPeerConnection === 'undefined') {
      resolve({ isBehindNat: false, publicIp: null })
      return
    }

    let resolved = false
    let pc: RTCPeerConnection | null = null
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const finish = (isBehindNat: boolean, publicIp: string | null) => {
      if (resolved) return
      resolved = true
      if (timeoutId) clearTimeout(timeoutId)
      if (pc) {
        pc.onicecandidate = null
        pc.onicegatheringstatechange = null
        pc.close()
        pc = null
      }
      resolve({ isBehindNat, publicIp })
    }

    try {
      pc = new RTCPeerConnection({ iceServers: [{ urls: STUN_SERVER }] })
      pc.createDataChannel('')

      pc.onicecandidate = (event) => {
        if (!event.candidate?.candidate) return
        const candidateStr = event.candidate.candidate
        if (candidateStr.includes('srflx')) {
          const ipMatch = candidateStr.match(/(\d{1,3}\.){3}\d{1,3}/)
          const ip = ipMatch ? ipMatch[0] : null
          // srflx with private IP = symmetric NAT (SER-4713: Motilal Oswal case)
          if (ip && isPrivateIp(ip)) {
            finish(true, ip)
          } else {
            finish(false, ip)
          }
        }
      }

      pc.onicegatheringstatechange = () => {
        if (pc?.iceGatheringState === 'complete') {
          finish(true, null)
        }
      }

      pc.createOffer()
        .then((offer) => pc?.setLocalDescription(offer))
        .catch(() => finish(true, null))

      timeoutId = setTimeout(() => {
        finish(true, null)
      }, ICE_GATHERING_TIMEOUT)
    } catch {
      finish(true, null)
    }
  })
}

export const useNatDetection = () => {
  const [result, setResult] = useState<NatDetectionResult>(() => {
    // Never probe in embed: the probe asks a third-party STUN server, and a partner's iframe
    // is not ours to send that from. The embed test call uses Plivo STUN only.
    if (isEmbedMode()) {
      return defaultResult
    }
    const cached = getCachedResult()
    if (cached) {
      return { ...cached, isChecking: false, hasError: false }
    }
    return { ...defaultResult, isChecking: true }
  })

  const runChecks = useCallback(async () => {
    if (isEmbedMode()) {
      return
    }
    setResult((prev) => ({ ...prev, isChecking: true }))

    try {
      const stunResult = await checkStunConnectivity()
      agentLogger.info('useNatDetection: STUN check complete', {
        isBehindNat: stunResult.isBehindNat,
        hasPublicIp: Boolean(stunResult.publicIp),
      })
      const cached: CachedResult = {
        isBehindNat: stunResult.isBehindNat,
        publicIp: stunResult.publicIp,
      }
      setCachedResult(cached)
      setResult({ ...cached, isChecking: false, hasError: false })
    } catch {
      setResult({ ...defaultResult, hasError: true })
    }
  }, [])

  const recheck = useCallback(() => {
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY)
    } catch {
      // ignore
    }
    runChecks()
  }, [runChecks])

  useEffect(() => {
    if (isEmbedMode()) {
      return
    }
    const cached = getCachedResult()
    if (cached) {
      setResult({ ...cached, isChecking: false, hasError: false })
      return
    }

    runChecks()
  }, [runChecks])

  return { ...result, recheck }
}
